import React, { useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import type {
  DateDropdownOption,
  ExportDateFieldValue,
} from "../../helpers/dataExportDateHelpers";
import { displayLabelForField } from "../../helpers/dataExportDateHelpers";
import { joinClasses } from "../../utils/styling";
import "./DataExportDateDropdown.scss";

interface DataExportDateDropdownProps {
  label: string;
  options: DateDropdownOption[];
  value: ExportDateFieldValue;
  disabled?: boolean;
  onChange: (value: ExportDateFieldValue) => void;
  width?: number;
  /** Earliest selectable custom date (YYYY-MM-DD), applied to the picker. */
  min?: string;
  /** Latest selectable custom date (YYYY-MM-DD), applied to the picker. */
  max?: string;
}

function DataExportDateDropdown({
  label,
  options,
  value,
  disabled = false,
  onChange,
  width = 200,
  min,
  max,
}: DataExportDateDropdownProps) {
  const [open, setOpen] = useState(false);

  const collapsedLabel = displayLabelForField(value, options);

  const selectPreset = (preset: DateDropdownOption["value"]) => {
    onChange({ preset, customDate: "" });
    setOpen(false);
  };

  return (
    <div className="DataExportDateDropdown" style={{ width }}>
      <label
        className="form-label mb-1 small text-uppercase text-muted"
        style={{ letterSpacing: "0.04em", fontWeight: 600 }}
      >
        {label}
      </label>
      <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
        <button
          type="button"
          className={joinClasses([
            "form-control",
            "form-control-sm",
            "DataExportDateDropdown__control",
            disabled ? "DataExportDateDropdown__control--disabled" : null,
          ])}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-disabled={disabled}
          aria-label={label}
          tabIndex={disabled ? -1 : 0}
          style={{ cursor: disabled ? "not-allowed" : "pointer" }}
          title={
            disabled
              ? "The selected export type doesn't support date ranges"
              : undefined
          }
          onMouseDown={(e) => {
            if (disabled) e.preventDefault();
          }}
          onClick={() => !disabled && setOpen((prev) => !prev)}
        >
          <span className="DataExportDateDropdown__value">
            {collapsedLabel}
          </span>
          <i
            className="DataExportDateDropdown__chevron fas fa-chevron-down"
            aria-hidden="true"
          />
        </button>

        {open && !disabled && (
          <div className="DataExportDateDropdown__menu" role="listbox">
            <div className="DataExportDateDropdown__heading DataExportDateDropdown__heading--timeframe">
              Select a Timeframe
            </div>
            {options.map((option) => {
              const active =
                value.preset === option.value && value.preset !== "custom";
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={joinClasses([
                    "DataExportDateDropdown__option",
                    active ? "DataExportDateDropdown__option--active" : null,
                  ])}
                  onClick={() => selectPreset(option.value)}
                >
                  {option.label}
                </button>
              );
            })}

            <div className="DataExportDateDropdown__divider" />
            <div className="DataExportDateDropdown__heading">
              Custom Date Range
            </div>
            <div className="DataExportDateDropdown__custom">
              <input
                type="date"
                className="form-control form-control-sm"
                aria-label={`${label} custom date`}
                value={value.preset === "custom" ? value.customDate : ""}
                min={min}
                max={max}
                onChange={(e) =>
                  onChange({ preset: "custom", customDate: e.target.value })
                }
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}
      </OutsideClickHandler>
    </div>
  );
}

export default DataExportDateDropdown;
